/**
 * User Slice
 * Manages user authentication state in Redux
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '@/lib/api/auth.api';
import { apiClient } from '@/lib/api/client';
import type { ApiError } from '@/types/common.types';

export interface User {
  id: string;
  email: string;
  name: string;
  displayName?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  isOperator: boolean;
  isTourist: boolean;
  status: 'INACTIVE' | 'ACTIVE' | 'DELETED';
  emailVerified: boolean;
  phoneVerified: boolean;
  isOperatorVerified: boolean;
  hadAddedBoats: boolean;
}

interface UserState {
  user: User | null;
  isLoading: boolean;
  isLoadingUser: boolean; // Flag to prevent concurrent loadUser calls
  isAuthenticated: boolean;
  error: ApiError | null;
}

const initialState: UserState = {
  user: null,
  isLoading: false, // Start as false, will be set to true when loading starts
  isLoadingUser: false, // Track if loadUser is in progress
  isAuthenticated: false,
  error: null,
};

/**
 * Load user from API
 * Includes deduplication to prevent multiple simultaneous calls
 */
export const loadUser = createAsyncThunk(
  'user/loadUser',
  async (_, { rejectWithValue, getState }) => {
    // Check if user is already loaded and we have a token
    const state = getState() as { user: UserState };
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    if (state.user.user && state.user.isAuthenticated && token) {
      // User already loaded, skip API call
      return state.user.user;
    }

    try {
      const response = await authApi.getCurrentUser();
      if (response.success && response.data) {
        // getCurrentUser returns user directly, not wrapped
        return response.data as User;
      }
      apiClient.removeToken();
      const errorPayload = response.error || { code: 'LOAD_ERROR', message: 'Failed to load user' };
      return rejectWithValue(errorPayload);
    } catch (error) {
      apiClient.removeToken();
      const errorPayload = {
        code: 'LOAD_ERROR',
        message: error instanceof Error ? error.message : 'Failed to load user',
      };
      return rejectWithValue(errorPayload);
    }
  }
);

/**
 * Login user
 */
export const loginUser = createAsyncThunk(
  'user/login',
  async (credentials: { email: string; password: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await authApi.login(credentials);
      if (response.success && response.data) {
        // After login, load full user data to get all fields
        const loadResult = await dispatch(loadUser());
        if (loadUser.fulfilled.match(loadResult)) {
          return loadResult.payload;
        }
        // If loadUser fails, return a basic user object from login response
        const userData = response.data.user;
        return {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          displayName: userData.name,
          phone: userData.phone,
          isOperator: false,
          isTourist: true,
          status: 'ACTIVE' as const,
          emailVerified: false,
          phoneVerified: false,
          isOperatorVerified: false,
          hadAddedBoats: false,
        } as User;
      }
      return rejectWithValue(response.error || { code: 'LOGIN_ERROR', message: 'Login failed' });
    } catch (error) {
      return rejectWithValue({
        code: 'LOGIN_ERROR',
        message: error instanceof Error ? error.message : 'Login failed',
      });
    }
  }
);

/**
 * Register user
 */
export const registerUser = createAsyncThunk(
  'user/register',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await authApi.register(data);
      if (response.success && response.data) {
        // After registration, load full user data to get all fields
        const loadResult = await dispatch(loadUser());
        if (loadUser.fulfilled.match(loadResult)) {
          return loadResult.payload;
        }
        // If loadUser fails, return a basic user object from register response
        const userData = response.data.user;
        return {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          displayName: userData.name,
          phone: userData.phone,
          isOperator: false,
          isTourist: true,
          status: 'ACTIVE' as const,
          emailVerified: false,
          phoneVerified: false,
          isOperatorVerified: false,
          hadAddedBoats: false,
        } as User;
      }
      return rejectWithValue(response.error || { code: 'REGISTER_ERROR', message: 'Registration failed' });
    } catch (error) {
      return rejectWithValue({
        code: 'REGISTER_ERROR',
        message: error instanceof Error ? error.message : 'Registration failed',
      });
    }
  }
);

/**
 * Logout user
 */
export const logoutUser = createAsyncThunk(
  'user/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout();
      apiClient.removeToken();
      return null;
    } catch (error) {
      // Even if logout API fails, clear local state
      apiClient.removeToken();
      return null;
    }
  }
);

/**
 * Refresh user data
 */
export const refreshUser = createAsyncThunk<User>(
  'user/refresh',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.getCurrentUser();
      if (response.success && response.data) {
        return response.data as User;
      }
      return rejectWithValue(response.error || { code: 'REFRESH_ERROR', message: 'Failed to refresh user' });
    } catch (error) {
      return rejectWithValue({
        code: 'REFRESH_ERROR',
        message: error instanceof Error ? error.message : 'Failed to refresh user',
      });
    }
  }
);

/**
 * Send operator OTP
 */
export const sendOperatorOTP = createAsyncThunk(
  'user/sendOperatorOTP',
  async (params: { email: string; type: 'LOGIN' | 'SIGNUP'; isDummyMode: boolean }, { rejectWithValue }) => {
    try {
      const response = await authApi.sendOperatorOTP(params.email, params.type, params.isDummyMode);
      if (response.success) {
        return { success: true };
      }
      return rejectWithValue(response.error || { code: 'OTP_SEND_ERROR', message: 'Failed to send OTP' });
    } catch (error) {
      return rejectWithValue({
        code: 'OTP_SEND_ERROR',
        message: error instanceof Error ? error.message : 'Failed to send OTP',
      });
    }
  }
);

/**
 * Verify operator OTP
 */
export const verifyOperatorOTP = createAsyncThunk(
  'user/verifyOperatorOTP',
  async (
    params: {
      email: string;
      code: string;
      type: 'LOGIN' | 'SIGNUP';
      name?: string;
      isDummyMode: boolean;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await authApi.verifyOperatorOTP(
        params.email,
        params.code,
        params.type,
        params.name,
        params.isDummyMode
      );
      if (response.success && response.data) {
        // After OTP verification, load full user data to get all fields
        const loadResult = await dispatch(loadUser());
        if (loadUser.fulfilled.match(loadResult)) {
          console.log('[verifyOperatorOTP] loadUser succeeded, isOperator:', loadResult.payload.isOperator);
          return loadResult.payload;
        }
        // If loadUser fails, return user object from verify response (includes correct isOperator flag)
        const userData = response.data.user;
        console.log('[verifyOperatorOTP] loadUser failed, using response data. isOperator:', userData?.isOperator, 'userData:', userData);
        return {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          displayName: userData.displayName || userData.name,
          phone: userData.phone,
          isOperator: userData.isOperator ?? false, // Use actual value from API, not hardcoded false
          isTourist: userData.isTourist ?? true,
          status: userData.status || 'ACTIVE',
          emailVerified: userData.emailVerified ?? false,
          phoneVerified: userData.phoneVerified ?? false,
          isOperatorVerified: userData.isOperatorVerified ?? false,
          hadAddedBoats: userData.hadAddedBoats ?? false,
        } as User;
      }
      return rejectWithValue(response.error || { code: 'OTP_VERIFY_ERROR', message: 'Failed to verify OTP' });
    } catch (error) {
      return rejectWithValue({
        code: 'OTP_VERIFY_ERROR',
        message: error instanceof Error ? error.message : 'Failed to verify OTP',
      });
    }
  }
);

export const authenticateWithGoogle = createAsyncThunk(
  'user/authenticateWithGoogle',
  async (
    params: { role: 'tourist' | 'operator'; credential: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await authApi.authenticateWithGoogle(params.role, params.credential);
      if (response.success && response.data) {
        const loadResult = await dispatch(loadUser());
        if (loadUser.fulfilled.match(loadResult)) {
          return loadResult.payload;
        }

        const userData = response.data.user;
        return {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          displayName: userData.displayName || userData.name,
          phone: userData.phone,
          address: userData.address,
          isOperator: userData.isOperator ?? false,
          isTourist: userData.isTourist ?? true,
          status: userData.status || 'ACTIVE',
          emailVerified: userData.emailVerified ?? true,
          phoneVerified: userData.phoneVerified ?? false,
          isOperatorVerified: userData.isOperatorVerified ?? false,
          hadAddedBoats: userData.hadAddedBoats ?? false,
        } as User;
      }

      return rejectWithValue(
        response.error || {
          code: 'GOOGLE_AUTH_ERROR',
          message: 'Failed to authenticate with Google',
        }
      );
    } catch (error) {
      return rejectWithValue({
        code: 'GOOGLE_AUTH_ERROR',
        message: error instanceof Error ? error.message : 'Failed to authenticate with Google',
      });
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.error = null;
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.isLoadingUser = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<ApiError | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // loadUser
    builder
      .addCase(loadUser.pending, (state) => {
        state.isLoading = true;
        state.isLoadingUser = true;
        state.error = null;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.isLoadingUser = false;
        state.error = null;
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.isLoadingUser = false;
        state.error = action.payload as ApiError;
      });

    // loginUser
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as ApiError;
      });

    // registerUser
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as ApiError;
      });

    // logoutUser
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout fails, clear user state
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
      });

    // refreshUser
    builder
      .addCase(refreshUser.pending, (state) => {
        state.error = null;
      })
      .addCase(refreshUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(refreshUser.rejected, (state, action) => {
        state.error = action.payload as ApiError;
      });

    // sendOperatorOTP
    builder
      .addCase(sendOperatorOTP.pending, (state) => {
        state.error = null;
      })
      .addCase(sendOperatorOTP.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(sendOperatorOTP.rejected, (state, action) => {
        state.error = action.payload as ApiError;
      });

    // verifyOperatorOTP
    builder
      .addCase(verifyOperatorOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOperatorOTP.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(verifyOperatorOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as ApiError;
      })

    // authenticateWithGoogle
      .addCase(authenticateWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authenticateWithGoogle.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(authenticateWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as ApiError;
      });
  },
});

export const { setUser, clearUser, setLoading, setError } = userSlice.actions;
export default userSlice.reducer;
